import { useEffect, useRef } from 'react';

/**
 * Hardware scanners in keyboard-wedge mode type the code and press Enter
 * faster than any hand can (docs/scanner.md §2, ported from pos). The
 * reducer is pure: a keydown in, a possible emitted code out. A burst is
 * accepted when every key came within SCANNER_MAX_INTER_KEY_MS of the last
 * and it is at least SCANNER_MIN_SCAN_LENGTH long; human typing never
 * qualifies, so the rest of the app's inputs are untouched.
 */

export const SCANNER_MAX_INTER_KEY_MS = 40;
export const SCANNER_MIN_SCAN_LENGTH = 6;
export const DUPLICATE_SCAN_SUPPRESS_MS = 1500;

export interface ScanBufferState {
  chars: string;
  lastKeyAt: number;
}

export const EMPTY_SCAN_BUFFER: ScanBufferState = { chars: '', lastKeyAt: 0 };

export function reduceScanKeydown(state: ScanBufferState, event: { key: string; now: number }): { next: ScanBufferState; emit: string | null } {
  const gap = event.now - state.lastKeyAt;
  const resetBuffer = gap > SCANNER_MAX_INTER_KEY_MS;
  if (event.key === 'Enter') {
    const candidate = resetBuffer ? '' : state.chars;
    return { next: { chars: '', lastKeyAt: event.now }, emit: candidate.length >= SCANNER_MIN_SCAN_LENGTH ? candidate : null };
  }
  // Modifier, arrow and function keys neither extend a burst nor break one: Shift held while a scanner types uppercase is normal.
  if (event.key.length !== 1) return { next: state, emit: null };
  return { next: { chars: (resetBuffer ? '' : state.chars) + event.key, lastKeyAt: event.now }, emit: null };
}

export interface LastScan {
  raw: string;
  at: number;
}

/** UI-side suppression of the same code twice in quick succession; the server's idempotency and first-admission rules are unaffected. */
export function isDuplicateScan(last: LastScan | null, raw: string, now: number): boolean {
  return last !== null && last.raw === raw && now - last.at < DUPLICATE_SCAN_SUPPRESS_MS;
}

export function useHidScannerInput(onScan: (raw: string) => void, enabled = true): void {
  const bufferRef = useRef<ScanBufferState>(EMPTY_SCAN_BUFFER);
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;
  useEffect(() => {
    if (!enabled) return;
    const handle = (event: KeyboardEvent) => {
      const { next, emit } = reduceScanKeydown(bufferRef.current, { key: event.key, now: Date.now() });
      bufferRef.current = next;
      if (emit !== null) {
        event.preventDefault();
        event.stopPropagation();
        onScanRef.current(emit);
      }
    };
    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  }, [enabled]);
}
